import { BaseApiService } from '@/shared/api/BaseApiService';
import type { IAuthPort } from '../domain/ports/auth.port';

export interface LoginResponse {
  accessToken?: string;
  mfaSessionToken?: string;
  user?: Record<string, unknown>;
  assignedClinics?: Array<{ clinicId: string; clinicName: string }>;
  message?: string;
}

export interface MfaChallengeResponse {
  accessToken: string;
  user?: Record<string, unknown>;
}

export interface SelectClinicResponse {
  accessToken: string;
  clinicId: string;
  clinicName?: string;
}

export interface RefreshResponse {
  accessToken: string;
}

export interface CurrentUserResponse {
  id: string;
  email: string;
  displayName: string | null;
  role: string;
  organizationId: string;
  clinicIds: string[];
  activeClinicId: string | null;
  clinics: Array<{ id: string; displayName: string; status: string }>;
}

export class AuthService extends BaseApiService implements IAuthPort {
  protected readonly resourcePath = 'public/auth';

  async login(email: string, password: string): Promise<LoginResponse> {
    return this.post('/login', { email, password }, {
      skipAuth: true,
      skipSessionInvalidation: true,
      withCredentials: true,
    });
  }

  async completeMfa(code: string, mfaSessionToken: string): Promise<MfaChallengeResponse> {
    return this.post(
      '/mfa/challenge',
      { code },
      {
        headers: { Authorization: `Bearer ${mfaSessionToken}` },
        skipAuth: true,
        skipSessionInvalidation: true,
        withCredentials: true,
      },
    );
  }

  async selectClinic(clinicId: string): Promise<SelectClinicResponse> {
    return this.post('/clinic/select', { clinicId });
  }

  async refresh(): Promise<RefreshResponse> {
    return this.post('/refresh', undefined, {
      withCredentials: true,
      skipAuth: true,
      skipSessionInvalidation: true,
    });
  }

  async getCurrentUser(): Promise<CurrentUserResponse> {
    return this.get('/me');
  }

  async logout(): Promise<void> {
    await this.post('/logout', undefined, {
      skipSessionInvalidation: true,
      withCredentials: true,
    });
  }
}
